import { Model, Factory, trait } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import {
  rand,
  randFullName,
  randStreetAddress,
  randCity,
  randZipCode,
  randChanceBoolean,
} from '@ngneat/falso'

import type { SchemaAttributes } from '../types.js'
import { filterable, pageable } from './utils.js'

import type { MirageServer, MockServer } from './server.js'

export type SearchResultData = SchemaAttributes<'SearchResult'>

export const model = Model as ModelDefinition<SearchResultData>

export const factory = Factory.extend<SearchResultData>({
  result_id(index: number) {
    return (index + 1).toString()
  },
  result_type() {
    return rand([
      'CLIENT',
      'PATIENT',
      'ITEM',
      'BUNDLE',
      'DISCHARGE_NOTE_TEMPLATE',
    ])
  },
  label(index: number) {
    return `${randFullName()} (${index}), ${randStreetAddress()}, ${randCity()}, ${randZipCode()}`
  },
  legacy_url() {
    return '#'
  },
  archived() {
    return randChanceBoolean({ chanceTrue: 0.3 })
  },
  extra() {
    return {}
  },

  withSecondOwner: trait({
    extra() {
      return {
        second_owner: randFullName(),
      }
    },
  }),
})

export function routes(server: MockServer) {
  server.get('/search', function (this: MirageServer, schema, request) {
    const searchResults = schema.all('searchResult')

    // filter first
    const filtered = filterable(searchResults, request, {
      search: (item, value) => {
        if (
          typeof item.extra.second_owner === 'string' &&
          item.extra.second_owner.toLowerCase().includes(value.toLowerCase())
        ) {
          return true
        }

        return item.label.toLowerCase().includes(value.toLowerCase())
      },
      'result_type.in': (item, value) => item.result_type === value,
      this_department: (item) => parseInt(item.result_id) % 2 === 0,
      department_group_id: (item) => parseInt(item.result_id) % 2 !== 0,
    })

    // then we paginate
    const { data, meta } = pageable(filtered, request)

    // and then we construct the response
    return {
      ...this.serialize(data),
      meta: {
        pagination: meta,
      },
    }
  })
}

export function seeds(server: MockServer) {
  server.createList('searchResult', 20)
  server.createList('searchResult', 10, 'withSecondOwner')
}
